import React, { useState, useEffect } from 'react';

const POSITION_GROUPS = [
  { label: 'Maalivahdit', match: (p) => ['G', 'GOALIE', 'GOALKEEPER'].includes((p || '').trim().toUpperCase()) },
  { label: 'Puolustajat', match: (p) => ['D', 'DEFENSE', 'DEFENSEMAN'].includes((p || '').trim().toUpperCase()) },
  { label: 'Hyökkääjät', match: (p) => ['F', 'FORWARD', 'LW', 'RW', 'C', 'CENTER'].includes((p || '').trim().toUpperCase()) },
];

function sortPlayersByPosition(players) {
  const positionOrder = (pos) => {
    const p = (pos || '').trim().toUpperCase();
    if (['G', 'GOALIE', 'GOALKEEPER'].includes(p)) return 0;
    if (['D', 'DEFENSE', 'DEFENSEMAN'].includes(p)) return 1;
    if (['F', 'FORWARD', 'LW', 'RW', 'C', 'CENTER'].includes(p)) return 2;
    return 3;
  };
  return players.slice().sort((a, b) => {
    const posDiff = positionOrder(a.position) - positionOrder(b.position);
    if (posDiff !== 0) return posDiff;
    return Number(a.number || 0) - Number(b.number || 0);
  });
}

function RosterByPosition({ players }) {
  return (
    <div className="roster-by-position">
      {POSITION_GROUPS.map((group) => {
        const groupPlayers = players.filter((p) => group.match(p.position));
        if (groupPlayers.length === 0) return null;
        return (
          <div key={group.label} className="roster-group">
            <h4 className="roster-group-label">{group.label}</h4>
            <ul className="team-players-list">
              {groupPlayers.map((player) => (
                <li key={player.id}>
                  {player.number ? <span className="player-number">#{player.number}</span> : null}
                  {player.name}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

function Teams({ teams, players, socket, user }) {
  const [newTeam, setNewTeam] = useState('');
  const [deletedTeams, setDeletedTeams] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);

  const addTeam = (e) => {
    e.preventDefault();
    if (newTeam.trim() === '' || !socket) return;
    socket.emit('addTeam', { name: newTeam });
    setNewTeam('');
  };

  const getPlayersForTeam = (teamId) =>
    sortPlayersByPosition(players.filter((player) => Number(player.team_id) === Number(teamId)));

  useEffect(() => {
    if (!socket) return;

    const handleDeletedTeams = (teamsData) => {
      setDeletedTeams(teamsData);
    };

    socket.on('deletedTeams', handleDeletedTeams);
    return () => {
      socket.off('deletedTeams', handleDeletedTeams);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket || !showDeleted) return;
    socket.emit('getDeletedTeams');
  }, [socket, showDeleted]);

  const deleteTeam = (teamId, teamName) => {
    if (!socket) return;
    if (!window.confirm(`Poistetaanko joukkue ${teamName}? Joukkue siirretään poistettuihin.`)) {
      return;
    }
    socket.emit('deleteTeam', teamId);
  };

  const restoreTeam = (teamId) => {
    if (!socket) return;
    socket.emit('restoreTeam', teamId);
  };

  const canEdit = user && (user.role === 'SuperAdmin' || user.role === 'Admin' || user.role === 'AdminTournament');

  return (
    <div className="teams-section">
      <h2>Joukkueet</h2>
      <div className="teams-toolbar">
        <button type="button" onClick={() => setShowDeleted(false)}>
          Aktiiviset joukkueet
        </button>
        <button type="button" onClick={() => setShowDeleted(true)}>
          Poistetut joukkueet
        </button>
      </div>
      {!showDeleted ? (
        <div className="team-list">
          {teams.map((team) => {
            const teamPlayers = getPlayersForTeam(team.id);
            return (
              <div key={team.id} className="team-card">
                <div className="team-card-header">
                  <h3>{team.name}</h3>
                  <span>{teamPlayers.length} pelaajaa</span>
                </div>
                {teamPlayers.length > 0 ? (
                  <RosterByPosition players={teamPlayers} />
                ) : (
                  <p className="team-no-players">Tälle joukkueelle ei ole pelaajia.</p>
                )}
                {canEdit && (
                  <button type="button" className="delete-button" onClick={() => deleteTeam(team.id, team.name)}>
                    Poista
                  </button>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="team-list">
          {deletedTeams.length > 0 ? deletedTeams.map((team) => (
            <div key={team.id} className="team-card deleted">
              <div className="team-card-header">
                <h3>{team.name}</h3>
                <span>Poistettu</span>
              </div>
              {canEdit && (
                <button type="button" onClick={() => restoreTeam(team.id)}>
                  Palauta
                </button>
              )}
            </div>
          )) : (
            <p>Poistettuja joukkueita ei löytynyt.</p>
          )}
        </div>
      )}
      {canEdit && (
        <form onSubmit={addTeam}>
          <input
            type="text"
            value={newTeam}
            onChange={(e) => setNewTeam(e.target.value)}
            placeholder="Anna uuden joukkueen nimi"
          />
          <button type="submit">Lisää joukkue</button>
        </form>
      )}
    </div>
  );
}

export default Teams;
